'use strict';

import { observable, configure, action, observe, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import termStore from '@uw-it-sis/lib-react/lib/TermStore';
import noticesStore from '@uw-it-sis/lib-react-myplan/lib/NoticesStore';
import {getAggregateState} from '@uw-it-sis/lib-react/lib/StateUtils';
import {isFalse} from '@uw-it-sis/lib-react/lib/Utils';

import registrationDetailsStore from './RegistrationDetailsStore';
import TermUtils from '@uw-it-sis/lib-react/lib/TermUtils';

/**
 * Store for data for the front page of registration
 */
class RegistrationStore {

    state = States.initial;

    defaultRegistrationTerm = "";

    constructor() {
        makeObservable(this, {
            state: observable,
            defaultRegistrationTerm: observable,
            mount: action,
            checkDataLoadStates: action,
            _calculateDefaultRegistrationTerm: action
        });

        this.dataLoadStates = {
            internalProcessing: States.initial,
            publishedTerms: States.initial,
            currentTerm: States.initial,
            notices: States.initial
        };
    }

    mount() {

        // Set initial internal states to pending
        this.dataLoadStates = {
            internalProcessing: States.pending,
            publishedTerms: States.pending,
            currentTerm: States.pending,
            notices: States.pending
        };

        this.checkDataLoadStates();


        this.termDisposer = observe(termStore, "state", function(change) {
            this.dataLoadStates.currentTerm = change.newValue;
            this.dataLoadStates.publishedTerms = change.newValue;

            if (this.dataLoadStates.publishedTerms === States.done) {
                this._calculateDefaultRegistrationTerm();
            }
            this.checkDataLoadStates();
        }.bind(this));

        termStore.load('currentTerm','publishedTerms');
        this.noticesDisposer = observe(noticesStore, "state", function(change) {
            this.dataLoadStates.notices = change.newValue;

            this.checkDataLoadStates();
        }.bind(this));

        noticesStore.mount();

        this.dataLoadStates.internalProcessing = States.done;

        this.checkDataLoadStates();

    }

    unmount() {
        this.termDisposer();
        // this.publishedTermsDisposer();
        this.noticesDisposer();
    }

    /**
     * Check aggregate data load states and update the overall state if necessary.
     */
    checkDataLoadStates() {
        this.state = getAggregateState(this.dataLoadStates);
    }

    /**
     * Pass-thru to RegistrationDetailsStore
     */
    refreshRegistrationDetails() {
        registrationDetailsStore.refreshData(true);
    }

    _calculateDefaultRegistrationTerm() {
        let publishedTerms = this.getPublishedTerms();

        if ( publishedTerms && publishedTerms.length > 0 ) {
            this.defaultRegistrationTerm = publishedTerms[0].id;
        }
    }

    /**
     * Determine the term id to be displayed based on existing term data and any term id discovered in the url
     *
     * @param urlTerm a term id from the url, may be undefined
     *
     * @return a map with these properties:
     *          termId : the term id, if one could be determined
     *          isPublished : true if a valid termId could be determined and it is a published term
     *          errorMsgs : an array of any error messages for problems during calculation
     */
    calculateDisplayTerm(urlTerm) {

        let result = {
            termId : undefined,
            isPublished : false,
            errorMsgs : []
        };

        // The term id for which the current page is displaying
        let publishedTerms = this.getPublishedTerms();

        // Check if a term exists as a param of the url
        if ( urlTerm ) {
            // term URL param exists
            if (! TermUtils.isValidFormattedTermId(urlTerm)) {
                // not in termID format; try to convert it to one
                result.termId = TermUtils.makeTermIdFromTermName(urlTerm);
            }
            // if the term id is invalid, generate an error message indicating so
            if ( result.termId === undefined ) {
                result.errorMsgs.push("The quarter specified does not exist");
            } else {
                // term id is valid, create a string term name (e.g. "Winter 2019")
                let termName = TermUtils.makeTermDescriptionFromTermId(result.termId);
                // Is the term id specified within the published terms data
                result.isPublished = publishedTerms.some(function (term) {
                    return term.id === result.termId;
                });
                // Term is not within the published data, generate error message
                if ( isFalse(result.isPublished) ) {
                    result.errorMsgs.push(`${termName} is currently not available`);
                }
            }
        } else {
            // there is no term from the url, default the term to the first published term
            if ( publishedTerms && publishedTerms.length > 0 ) {
                result.termId = this.defaultRegistrationTerm;
            } else {
                // for some reason there are no published terms, generate error message
                result.errorMsgs.push("There are currently no quarters published");
            }
        }

        return result;
    }

    /**
     * Pass-thru to CurrentTermStore
     */
    getCurrentTerm() {
        return termStore.get('currentTerm');
    }

    /**
     * Pass-thru to PublishedTermsStore
     */
    getPublishedTerms() {
        return termStore.get('publishedTerms');
    }
}

export default new RegistrationStore();